import { z } from 'zod';

/**
 * STAFF TASKS — the request shapes both sides of the API agree on.
 * docs/plans/staff-tasks.md.
 *
 * `dueDate` is a company DAY as `YYYY-MM-DD`, never an instant: "due on the
 * 6th" means the 6th in the company's time zone wherever the reader is
 * (docs/time-and-dates.md). The server parks it at UTC midnight like every
 * other `@db.Date` column.
 */

export const TaskStatusSchema = z.enum(['TODO', 'IN_PROGRESS', 'WAITING_REVIEW', 'DONE', 'CANCELLED']);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH']);
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;

/** A real calendar day: the shape, AND it survives a round trip (no 2026-02-31). */
const DayKeySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .refine((s) => {
    const d = new Date(`${s}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, 'That date does not exist');

export const TaskCreateSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).optional(),
  /** At least one person; the same person twice is one assignment. */
  assigneeIds: z.array(z.string().min(1)).min(1).max(50),
  dueDate: DayKeySchema,
  priority: TaskPrioritySchema.default('NORMAL'),
  requiresReview: z.boolean().default(false),
  allowAssigneeFiles: z.boolean().default(true),
  worksiteId: z.string().min(1).optional(),
  steps: z.array(z.string().trim().min(1).max(200)).max(30).default([]),
  /** Started from this template: the task records it, and gets its own copy of the template's file. */
  templateId: z.string().min(1).optional(),
});
export type TaskCreateInput = z.infer<typeof TaskCreateSchema>;

/**
 * A PERSONAL task — one somebody sets for themselves from My tasks. Only what
 * is theirs to say: no people (it is theirs), no review (nobody else sees it),
 * no site, no template. The server assigns it to the caller. STRICT: a body
 * naming people or a review is refused, never quietly half-honoured.
 */
export const PersonalTaskCreateSchema = TaskCreateSchema.pick({
  title: true,
  description: true,
  dueDate: true,
  priority: true,
  steps: true,
}).strict();
export type PersonalTaskCreateInput = z.infer<typeof PersonalTaskCreateSchema>;

/** Every field optional: a PATCH changes what it names and nothing else. */
export const TaskUpdateSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  dueDate: DayKeySchema.optional(),
  priority: TaskPrioritySchema.optional(),
  requiresReview: z.boolean().optional(),
  allowAssigneeFiles: z.boolean().optional(),
  /**
   * The WHOLE new list of people, for a reassign — open work only. Anyone new
   * on it must be in the caller's scope and have a login of their own.
   */
  assigneeIds: z.array(z.string().min(1)).min(1).max(50).optional(),
});
export type TaskUpdateInput = z.infer<typeof TaskUpdateSchema>;

/**
 * The board's piles. `open` is TODO + IN_PROGRESS (late included); `late` is
 * the open work whose due day has passed; `review` is WAITING_REVIEW. Each pile
 * is its own query, so a long history of finished work can never push new open
 * work past the list's cap.
 */
export const TaskPileSchema = z.enum(['open', 'late', 'review', 'done', 'cancelled']);
export type TaskPile = z.infer<typeof TaskPileSchema>;

/**
 * The admin board's filters. `overdue` is not a status — it is open work whose
 * due day is before the company's today — so it is its own switch rather than a
 * fake member of the status enum. `pile` defaults to `open` when neither
 * `status` nor `overdue` is given; `q` matches the title or an assignee's name.
 */
export const TaskListQuerySchema = z.object({
  pile: TaskPileSchema.optional(),
  status: TaskStatusSchema.optional(),
  overdue: z.enum(['true', 'false']).optional(),
  employeeId: z.string().min(1).optional(),
  q: z.string().trim().max(100).optional(),
});
export type TaskListQuery = z.infer<typeof TaskListQuerySchema>;

export const TaskStepToggleSchema = z.object({ done: z.boolean() });

export const TaskCommentSchema = z.object({ body: z.string().trim().min(1).max(4000) });

/**
 * Handing in. `note` is REQUIRED when steps are still open — the server checks
 * that, because only it knows the steps — and is what the reviewer reads.
 */
export const TaskHandInSchema = z.object({ note: z.string().trim().max(2000).optional() });

export const TaskSendBackSchema = z.object({ reason: z.string().trim().min(1).max(2000) });

// ── Templates and their schedules ──────────────────────────────────────────

export const TaskRepeatSchema = z.enum(['NONE', 'WEEKLY', 'MONTHLY']);

/**
 * A schedule rule. WEEKLY names a weekday (0–6, Sunday first — the JS day of
 * week, as `Company.weekendDays` stores it); MONTHLY names a day 1–28 or 0 for
 * the last day of the month (29–31 would silently skip short months).
 */
export const TaskScheduleRuleSchema = z
  .object({
    repeat: TaskRepeatSchema.default('NONE'),
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    monthDay: z.number().int().min(0).max(28).nullable().optional(),
    startsOn: DayKeySchema.nullable().optional(),
    endsOn: DayKeySchema.nullable().optional(),
    pausedUntil: DayKeySchema.nullable().optional(),
  })
  .refine((r) => r.repeat !== 'WEEKLY' || (r.weekday !== null && r.weekday !== undefined), {
    message: 'A weekly schedule needs a weekday',
    path: ['weekday'],
  })
  .refine((r) => r.repeat !== 'MONTHLY' || (r.monthDay !== null && r.monthDay !== undefined), {
    message: 'A monthly schedule needs a day of the month',
    path: ['monthDay'],
  })
  .refine((r) => !r.startsOn || !r.endsOn || r.startsOn <= r.endsOn, {
    message: 'The schedule ends before it starts',
    path: ['endsOn'],
  });
export type TaskScheduleRule = z.infer<typeof TaskScheduleRuleSchema>;

export const TaskTemplateInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).nullable().optional(),
  priority: TaskPrioritySchema.default('NORMAL'),
  dueAfterDays: z.number().int().min(0).max(60).default(0),
  requiresReview: z.boolean().default(false),
  allowAssigneeFiles: z.boolean().default(true),
  steps: z.array(z.string().trim().min(1).max(200)).max(30).default([]),
  /** Who a scheduled task goes to. Ignored by a template that does not repeat. */
  assigneeIds: z.array(z.string().min(1)).max(50).default([]),
  schedule: TaskScheduleRuleSchema.default({ repeat: 'NONE' }),
});
export type TaskTemplateInput = z.infer<typeof TaskTemplateInputSchema>;

/** The editor's preview: the next dates this rule would create tasks on. */
export const TaskSchedulePreviewSchema = z.object({
  schedule: TaskScheduleRuleSchema,
  dueAfterDays: z.number().int().min(0).max(60).default(0),
});
